/**
 * Screener landing page.
 *
 * Marketing-grade hero + estimated-time meta + topic preview list.
 * The "Start the screener" CTA invokes `onStart` which the routing
 * layer wires to navigate into the first question.
 */
import styles from './Screener.module.css';
import { screenerQuestions } from './screenerConfig';
import { ScreenerTopBar } from './ScreenerTopBar';

export interface IScreenerLandingProps {
  /** Fired when the user clicks the "Start" button. */
  onStart: () => void;
  /** Fired when the user clicks the persistent "Sign in" affordance
   *  in the top bar. When omitted the link is hidden. */
  onSignInClick?: () => void;
}

export function ScreenerLanding({
  onStart,
  onSignInClick,
}: IScreenerLandingProps): JSX.Element {
  // Show the first 8 distinct topic hints so the landing page is
  // honest about scope without spilling all 22 questions.
  const topics = uniqueTopics(screenerQuestions, 8);

  return (
    <main
      id="main-content"
      className={styles.screener}
      aria-labelledby="screener-landing-title"
    >
      <ScreenerTopBar
        metaLabel="Marriage-based green card"
        onSignInClick={onSignInClick}
      />
      <div className={styles.screener__container}>
        <section className={styles.screener__landing_hero}>
          <span className={styles.screener__landing_eyebrow}>
            Pre-qualification
          </span>
          <h1
            id="screener-landing-title"
            className={styles.screener__landing_title}
          >
            See if a marriage-based green card is the right path for you
          </h1>
          <p className={styles.screener__landing_lede}>
            Answer about 22 questions — most are yes/no. We'll tell you whether
            your case looks straightforward, where you might want an attorney,
            or if a different filing path fits better. We do not give legal
            advice.
          </p>

          <ul
            className={styles.screener__landing_meta}
            aria-label="What to expect"
          >
            <li className={styles.screener__landing_meta_item}>
              <span className={styles.screener__landing_meta_dot} />
              Takes 5–10 minutes
            </li>
            <li className={styles.screener__landing_meta_item}>
              <span className={styles.screener__landing_meta_dot} />
              No account required to finish
            </li>
            <li className={styles.screener__landing_meta_item}>
              <span className={styles.screener__landing_meta_dot} />
              Your answers stay on your device
            </li>
          </ul>

          <button
            type="button"
            className={styles.screener__landing_cta}
            onClick={onStart}
          >
            Start the screener →
          </button>
        </section>

        <section
          className={styles.screener__landing_topics}
          aria-labelledby="screener-topics-title"
        >
          <h2
            id="screener-topics-title"
            className={styles.screener__landing_topics_title}
          >
            What we'll ask about
          </h2>
          <ul className={styles.screener__landing_topics_list}>
            {topics.map(t => (
              <li key={t} className={styles.screener__landing_topics_item}>
                {t}
              </li>
            ))}
          </ul>
        </section>

        <p className={styles.screener__landing_disclaimer}>
          This screener is informational only. It is not legal advice and does
          not create an attorney–client relationship. A licensed immigration
          attorney can review your facts and recommend a path; we link to
          AILA's directory at the end of the flow if you want one.
        </p>
      </div>
    </main>
  );
}

function uniqueTopics(
  qs: ReadonlyArray<{ topicHint?: string }>,
  cap: number,
): readonly string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const q of qs) {
    if (!q.topicHint) continue;
    if (seen.has(q.topicHint)) continue;
    seen.add(q.topicHint);
    out.push(q.topicHint);
    if (out.length >= cap) break;
  }
  return out;
}
